import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl } from '@angular/forms';
import { ChartCompiledData, IndexComponentData, MeasureChartComponentData, ResolvedTypography } from '@core/data/types/advanced-report';
import { applyChartTypography } from '@core/fonts/chart-typography';
import { uuid } from '@core/utils';
import { BoxAndWiskers, BoxPlotController } from '@sgratzl/chartjs-chart-boxplot';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

Chart.register(
    ...registerables,
    BoxPlotController,
    BoxAndWiskers
);

Chart.defaults.font.family = 'Roboto';

@Component({
    selector: 'pv-generic-report-graph',
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
    templateUrl: 'generic-graph-component.html',
    styleUrls: ['generic-graph-component.scss']
})
export class GenericReportGraph implements AfterViewInit, OnDestroy, OnInit, OnChanges {

    @Input()
    set compiledData(data: ChartCompiledData) {
        if (this._compiledData$.getValue() !== data) this._compiledData$.next(data);
    }

    @Input()
    graphControl: FormControl;

    @Input()
    set typography(value: ResolvedTypography) {
        this._typography = value;
        // Re-emit so the chart rebuilds with the new font.
        this._compiledData$.next(this._compiledData$.getValue());
    }
    private _typography: ResolvedTypography;

    //? VIEW CHILDREN
    @ViewChild('genericGraphCanvas', { static: false })
    canvasRef: ElementRef;

    @ViewChild('genericGraphContainer', { static: false })
    containerRef: ElementRef;

    //? Plugin for short grid lines, source: https://youtu.be/EV9VUQQPNxw?si=wSoyUbCCuRE80tfw
    shortGridLines = {
        id: 'shortGridLines',
        beforeDatasetsDraw(chart, args, pluginOptions) {
            const { ctx, chartArea: {bottom, top}, scales: {x} } = chart;

            ctx.save();

            for (let i = 0; i < x._gridLineItems.length; i++) {
                ctx.beginPath();
                ctx.lineWidth = 1;
                ctx.strokeStyle = '#9c9c9c';
                ctx.moveTo(x._gridLineItems[i].tx1, bottom);
                ctx.lineTo(x._gridLineItems[i].tx1, bottom - (top / 10));
                ctx.stroke();
                ctx.closePath();
                ctx.restore();
            }
        }
    }

    showChart: boolean = true;
    chartId = uuid();

    private _canvas: HTMLCanvasElement;
    private _chart: Chart;
    private firstLoad: boolean = true;

    private _chartData$ = new BehaviorSubject<MeasureChartComponentData | IndexComponentData>(null);
    private _compiledData$ = new BehaviorSubject<ChartCompiledData>(null);
    private _devicePixelRatio$ = new BehaviorSubject<number>(null);
    private _destroy$ = new Subject();

    constructor(
        private _changes: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this._chartData$.next(this.graphControl.value);
    }

    ngOnChanges() {
        if (this.firstLoad) return;
        if (this.graphControl.value == this._chartData$.value) return;
        this._chartData$.next(this.graphControl.value);
    }

    ngAfterViewInit() {
        this.firstLoad = false;
        this.updateChart();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    private updateChart() {
        this._canvas = this.canvasRef.nativeElement;

        combineLatest(
            this._chartData$,
            this._compiledData$,
        ).pipe(
            takeUntil(this._destroy$),
            debounceTime(100),
        )
        .subscribe(changes => {

            let [data, compiled] = changes;

            if (!compiled) return this.showChart = false;
            else this.showChart = true;

            let chartData = JSON.parse(JSON.stringify(data));
            let compiledData: ChartCompiledData = JSON.parse(JSON.stringify(compiled));

            let graphConfig;
            let plugins = [];

            if (chartData.hasOwnProperty('chartConfig')) graphConfig = chartData.chartConfig;
            if (chartData.hasOwnProperty('indexConfig')) graphConfig = chartData.indexConfig;

            if (!graphConfig) return;

            let options: any = {
                ...graphConfig,
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    ...graphConfig?.scales,
                }
            }

            if (graphConfig.scales.hasOwnProperty('y')) {
                options = {
                    ...options,
                    scales: {
                        ...options.scales,
                        y: {
                            ...graphConfig.scales.y,
                            grid: {
                                ...graphConfig.scales.y.grid,
                                color: (context) => {
                                    if (context.index % 2 === 0) return '#9c9c9c';
                                    return '#e8e8e8';
                                }
                            }
                        }
                    }
                };

                plugins.push(this.shortGridLines);
            }

            // Force the report font onto every label scope (title/legend/ticks/point labels).
            options = applyChartTypography(options, this._typography);

            try {

                const config = this._chart ? this._chart.config as ChartConfiguration : null;
                const currentCanvas = this.canvasRef.nativeElement;

                if (this._chart && (config?.type !== chartData.type)) {
                    this._chart.destroy();
                    this._chart = null;
                }

                if (!this._chart) {
                    if (this._chart) this._chart.destroy();

                    this._chart = new Chart(currentCanvas, {
                        type: chartData.type,
                        options: options,
                        data: compiledData,
                        plugins,
                    });
                } else {
                    this._chart.options = { ...options as any };
                    this._chart.data = compiledData;
                    this._chart.update('none');
                }

                this._changes.markForCheck();

            } catch (error) {
                console.error("ChartComponent: Error compiling chart", error);

                if (this._chart) {
                    this._chart.destroy();
                    this._chart = null;
                }
            }
        });
    }
}